import React, {
  useState,
  useCallback,
  useMemo,
  useContext,
  useRef,
  useEffect,
} from 'react';
import { Formik } from 'formik';
import { observer } from 'mobx-react';
import * as Sentry from '@sentry/browser';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import PropTypes from 'prop-types';
import format from 'string-template';
import { useHistory } from 'react-router-dom';

import useComponentMounted from '../../../../../hooks/useComponentMounted';
import useSessionStore from '../../../../../hooks/useSessionStore';
import useClipboard from '../../../../../hooks/useClipboard';
import {
  HeaderRow,
  TitleContainer,
  Title,
} from '../../../../../components/v2/Header';
import {
  SectionContainer,
  SectionHeaderContainer,
  SectionTitle,
  SectionFooterContainer,
} from '../../../../../components/v2/Section';
import {
  SaveButton,
  CancelButton,
} from '../../../../../components/Button/ActionButtons';
import ConfirmDialog from '../../../../components/ConfirmDialog';
import DialogRoundedModal from '../../../../../components/DialogRoundedModal';
import Workout, { VisibilityType } from '../../../../../Model/Workout';
import WorkoutAssignment from '../../../../../Model/WorkoutAssignment';
import UserContext from '../../../../../context/UserContext';
import ExternalCoachContext from '../../../../context/ExternalCoachContext';
import ManageProgramContext from '../../../../context/ManageProgramContext';
import useLogger from '../../../../../hooks/useLogger';
import useTimeForActionLogger, { ActionEvent } from '../../../../../hooks/useTimeForActionLogger';
import { CoachingActivity } from '../../../../../utils/log';
import {
  validateWorkoutContent,
  WorkoutAssignmentCode,
} from '../../../../../utils/workout';
import {
  WorkoutPublishStatus,
  WorkoutSessionType,
  isLongSessionWorkout,
} from '../../../../../utils/longSession';
import { LongSessionUploadActionsContext } from '../../../../../context/LongSessionUploadContext';
import { FeatureFlag } from '../../../../../utils/postHog';
import { formatSeconds } from '../../../../../utils/date';
import { isUserFacingError } from '../../../../../utils/error';
import useToast from '../../../../hooks/useToast';
import ExercisesTable from '../../Exercises/ExercisesTable';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import ConfirmationModal from '../../../../../components/v2/ConfirmationModal';
import { ActionType } from '../utils';
import Activity from '../../../../../Model/Activity';
import WorkoutRequest from '../../../../../Model/WorkoutRequest';
import InputLabel from '../../../../../components/v2/InputLabel';
import {
  getWorkoutUsageCounts,
  isUnownedPublicProgram,
} from '../../Programs/utils';
import AIWorkoutGenerator from './AIWorkoutGenerator';
import SharedWorkoutDialog from './SharedWorkoutDialog';
import {
  initialValues as startValues,
  validationSchema,
} from './validation';
import fieldName from './formFields';
import {
  COMPONENT_REARRANGE_SCREEN_WIDTH,
  assignmentsUpdateOptions,
  AssignmentUpdateType,
  estimateDuration,
  hasDistanceActivities,
} from './utils';

import {
  FLASH_CLASSNAME,
  FLASH_SECONDS,
  Container,
  StyledForm,
  StyledSectionCompartment,
  ContentContainer,
  ScrollableContainer,
  StyledFormikInput,
  NameFieldRow,
  DurationContainer,
  EstimatedDurationText,
  DurationInputRow,
  DurationInput,
  DurationMinLabel,
  DurationAutoHint,
  StyledRadioButtonGroup,
  SessionTypeContainer,
  WorkoutSubDetailsContainer,
  WorkoutIdValue,
  ClipboardName,
  ClipboardExerciseItem,
} from './styles';
import texts from './texts';
import Activities from './Activities';
import LongSessionPanel from './LongSessionPanel';
import { CLIPBOARD_KEY } from './Activities/utils';

/**
 * The activities the editor starts from, whichever kind of edit this is: a library
 * workout, a workout assignment, or a workout that is scheduled but not sent yet.
 *
 * @param {Object} params - The editor's content sources.
 * @returns {Array<Object>} The activity items, as stored.
 */
const getInitialActivityItems = ({ workout, workoutAssignment, scheduledWorkoutDraft }) => (
  workout?.activities
  || workoutAssignment?.workoutContent?.activities
  || scheduledWorkoutDraft?.workoutContent?.activities
  || []
);

const SESSION_TYPE_OPTIONS = [
  { value: WorkoutSessionType.STANDARD, label: texts.sessionType[WorkoutSessionType.STANDARD] },
  { value: WorkoutSessionType.LONG_SESSION, label: texts.sessionType[WorkoutSessionType.LONG_SESSION] },
];

const WorkoutEditor = ({
  workout,
  workoutAssignment,
  scheduledWorkoutDraft,
  action,
  programDoc,
  dayIndex,
  programWorkoutDays,
  onSaveScheduledWorkoutDraft,
  onSave,
  onClose,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showExercisesTable, setShowExercisesTable] = useState(false);
  const [showAIPrompt, setShowAIPrompt] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [preparedActivities, setPreparedActivities] = useState(
    getInitialActivityItems({ workout, workoutAssignment, scheduledWorkoutDraft })
      .map((activityItem) => new Activity(activityItem)),
  );
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [formikData, setFormikData] = useState({});
  const [assignmentsToUpdate, setAssignmentsToUpdate] = useState([]);
  const [showAssignmentUpdateModal, setShowAssignmentUpdateModal] = useState(false);
  const [showSaveAsDraftModal, setShowSaveAsDraftModal] = useState(false);
  const [sharedWorkoutPrompt, setSharedWorkoutPrompt] = useState(null);
  const [selectedAssignmentUpdateOption, setSelectedAssignmentUpdateOption] = useState(
    AssignmentUpdateType.SELECTED_ASSIGNMENT,
  );
  const [workoutRequestId, setWorkoutRequestId] = useState('');
  const [generatedByAI, setGeneratedByAI] = useState(!!workout?.generatedByAI);
  const [navConfirmOpen, setNavConfirmOpen] = useState(false);
  const [pendingLocation, setPendingLocation] = useState(null);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [durationOverrideMinutes, setDurationOverrideMinutes] = useState(null);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const ref = useRef(null);
  const formikRef = useRef(null);
  const unblockRef = useRef(null);

  const history = useHistory();

  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();
  const { isAdmin, userId: loggedInUserId } = useSessionStore();
  const { clipboard } = useClipboard(CLIPBOARD_KEY);
  // The coach who owns this workout, from the /c/:userId route - NOT the logged-in user
  const { userDoc: { id: coachId } } = useContext(UserContext);
  const { coachDoc } = useContext(ExternalCoachContext);
  const { coachPrograms } = useContext(ManageProgramContext);
  const { logCoachingActivity } = useLogger();
  const { logSuccess: logWorkoutTime } = useTimeForActionLogger({
    actionEvent: ActionEvent.WORKOUT,
  });

  /*
    Read on every render rather than memoized on `workout`. A firestorter Document keeps the same
    identity while its data loads, so a memo keyed on it freezes whatever the first render saw —
    which, for a workout opened by link, is an empty snapshot. The editor then treats a long session
    as a standard workout: exercise rows, a duration field, no tracker, and a save that fails
    validation. Formik compares `initialValues` deeply, so a fresh object per render costs nothing.
  */
  const docData = workout?.data || workoutAssignment?.workoutContent || scheduledWorkoutDraft?.workoutContent || {};

  const isLongSessionEnabled = useFeatureFlagEnabled(FeatureFlag.LONG_WORKOUT_SESSION);
  const { cancelUpload } = useContext(LongSessionUploadActionsContext);

  /*
    Set only by the session-type selector. The type is otherwise *derived* from the workout rather
    than snapshotted into state at mount: `docData` arrives asynchronously (which is why Formik needs
    `enableReinitialize` below), so a one-shot initial value renders an existing long session as a
    standard workout — exercise rows, a duration field, no tracker, and a save that fails validation.
  */
  const [selectedSessionType, setSelectedSessionType] = useState(null);
  /* The workout created when the coach picks a video, for a session that had none before. */
  const [createdLongSessionWorkout, setCreatedLongSessionWorkout] = useState(null);

  const isSavedLongSession = isLongSessionWorkout(docData);
  /*
    Formik's `initialValues`, and with `enableReinitialize` any deep change resets the form. The server
    rewrites a long session at every processing stage, so handing it `docData` would wipe the name or
    note mid-edit. A saved long session only ever edits those two fields, so they are all it is given.
    Every other workout keeps `docData`: create and fork spread `values` into the new document.
  */
  const formInitialValues = isSavedLongSession
    ? {
      [fieldName.NAME]: docData[fieldName.NAME] || '',
      [fieldName.NOTE]: docData[fieldName.NOTE] || '',
    }
    : docData;
  const sessionType = selectedSessionType
    || (isSavedLongSession ? WorkoutSessionType.LONG_SESSION : WorkoutSessionType.STANDARD);
  const isLongSession = sessionType === WorkoutSessionType.LONG_SESSION;

  /*
    The session's workout, which for a long session exists before Save is ever pressed — it is
    created when the coach picks a video, because the upload path needs its id.
  */
  const longSessionWorkout = createdLongSessionWorkout || (isSavedLongSession ? workout : null);
  /* A long session whose workout exists: created the moment the coach picked a video. */
  const hasLongSessionWorkout = isLongSession && !!longSessionWorkout;
  /*
    A finished session keeps the plain Cancel the coach expects in that position — discarding it
    archives the workout, which is the workouts table's archive action.
    Read from the workout, not `docData`, which is still empty for a session just created here.
  */
  const canDiscardWorkout = hasLongSessionWorkout
    && longSessionWorkout.publishStatus !== WorkoutPublishStatus.READY;

  // Only offered when creating, and only until the shape is settled: picking a video creates the
  // workout, so flipping back to Standard afterwards would strand it mid-upload.
  const canPickSessionType = isLongSessionEnabled
    && action === ActionType.CREATE
    && !isSavedLongSession
    && !longSessionWorkout
    && !preparedActivities.length;

  const isPublicTracksCoach = !!coachDoc?.isPublicTracksCoach;
  const isEditingPublicWorkout = action === ActionType.UPDATE
    && workout?.visibility === VisibilityType.PUBLIC
    && !isPublicTracksCoach;

  const onClickAddActivity = useCallback(() => {
    const isSmallScreen = window.innerWidth <= COMPONENT_REARRANGE_SCREEN_WIDTH;

    if (showExercisesTable && ref.current) {
      // Scroll to the table if the screen size is below column wise width
      if (isSmallScreen) {
        ref.current.scrollIntoView({ behavior: 'smooth' });
      } else {
        // If the table is beside the designer, add the flash animation
        ref.current.classList.add(FLASH_CLASSNAME);
        // Remove the flash after the animation duration
        setTimeout(() => {
          ref.current.classList.remove(FLASH_CLASSNAME);
        }, FLASH_SECONDS * 1000);
      }
    } else {
      setShowAIPrompt(false);
      setShowExercisesTable(true);
      // Scroll to the table after a small delay to account for delay in opening table
      setTimeout(() => {
        if (isSmallScreen && ref.current) {
          ref.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 1000);
    }
  }, [
    showExercisesTable,
    ref,
  ]);

  const onClickAIPrompt = useCallback(() => {
    setShowAIPrompt(true);
    setShowExercisesTable(false);
  }, []);

  const handleSubmit = useCallback(async (values, actions, options = {}) => {
    let workoutId = workout?.id;
    let forkedWorkoutId = null;
    setIsLoading(true);
    try {
      const durationSeconds = durationOverrideMinutes !== null
        ? durationOverrideMinutes * 60
        : estimateDuration(preparedActivities);
      // This hold's the coach's current edits, linked to its source via `originalWorkout`
      const buildForkedWorkout = (name) => ({
        ...values,
        [fieldName.NAME]: name,
        coach: coachId,
        createdAt: Date.now(),
        activities: preparedActivities.map((activity) => activity.jsonActivity),
        visibility: VisibilityType.PRIVATE,
        estimatedDuration: durationSeconds,
        isArchived: false,
        originalWorkout: workout?.id,
        ...(generatedByAI && { generatedByAI }),
      });
      if (hasLongSessionWorkout) {
        /*
          The workout was created when the video was chosen, so Save is an update no matter which
          action opened the editor. Activities and duration are deliberately untouched: both are
          written by the server once the video is READY.
        */
        workoutId = longSessionWorkout.id;
        await longSessionWorkout.updateFields({
          [fieldName.NAME]: values[fieldName.NAME],
          [fieldName.NOTE]: values[fieldName.NOTE] || '',
        });
        showToast(format(texts.workoutUpdated, { workoutName: values[fieldName.NAME] }));
        logCoachingActivity(CoachingActivity.UPDATED_WORKOUT, { workoutId });
      } else if (action === ActionType.UPDATE) {
        // If the workout is PUBLIC, create a new workout instead of updating the original
        if (isEditingPublicWorkout) {
          workoutId = await Workout.addWorkout(buildForkedWorkout(values[fieldName.NAME]));
          showToast(format(texts.workoutCreated, { workoutName: values[fieldName.NAME] }));
          logCoachingActivity(CoachingActivity.CREATED_WORKOUT, { workoutId });
        } else if (options.createNewWorkout && programDoc) {
          // This workout is shared across programs/days. Fork a private copy with
          // the coach's changes, leaving the original untouched.
          const newWorkoutNameValue = options.newName || values[fieldName.NAME];
          workoutId = await Workout.addWorkout(buildForkedWorkout(newWorkoutNameValue));
          forkedWorkoutId = workoutId;
          showToast(format(texts.workoutCreated, { workoutName: newWorkoutNameValue }));
          logCoachingActivity(CoachingActivity.CREATED_WORKOUT, { workoutId });
        } else {
          // For PRIVATE workouts, update as normal
          await workout.updateFields({
            [fieldName.NAME]: values[fieldName.NAME],
            [fieldName.NOTE]: values[fieldName.NOTE] || '',
            activities: preparedActivities.map((activity) => activity.jsonActivity),
            estimatedDuration: durationSeconds,
            ...(generatedByAI && { generatedByAI }),
          });
          showToast(format(texts.workoutUpdated, {
            workoutName: values[fieldName.NAME],
          }));
          logCoachingActivity(CoachingActivity.UPDATED_WORKOUT, { workoutId: workout.id });
        }
      } else if (action === ActionType.CREATE) {
        workoutId = await Workout.addWorkout(
          {
            ...values,
            coach: coachId,
            createdAt: Date.now(),
            activities: preparedActivities.map((activity) => activity.jsonActivity),
            visibility: VisibilityType.PRIVATE,
            estimatedDuration: durationSeconds,
            isArchived: false,
            ...(generatedByAI && { generatedByAI }),
          },
        );
        showToast(format(texts.workoutCreated, { workoutName: values[fieldName.NAME] }));
        logCoachingActivity(CoachingActivity.CREATED_WORKOUT, { workoutId });
      } else if (action === ActionType.UPDATE_WORKOUT_ASSIGNMENT) {
        const updatedWorkoutContent = {
          [fieldName.NAME]: values[fieldName.NAME],
          [fieldName.NOTE]: values[fieldName.NOTE] || '',
          activities: preparedActivities.map((activity) => activity.jsonActivity),
          estimatedDuration: durationSeconds,
        };

        if (selectedAssignmentUpdateOption === AssignmentUpdateType.SELECTED_ASSIGNMENT) {
          await workoutAssignment.updateFields({
            workoutContent: updatedWorkoutContent,
          });
          showToast(format(texts.workoutAssignmentUpdated, { workoutName: values.name }));
        } else {
          await Promise.all(assignmentsToUpdate.map(async (assignment) => {
            await assignment.updateFields({
              workoutContent: updatedWorkoutContent,
            });
          }));
          showToast(format(texts.workoutAssignmentsUpdated, {
            workoutName: values.name,
            count: assignmentsToUpdate.length,
          }));
        }
        logCoachingActivity(CoachingActivity.UPDATED_ASSIGNED_WORKOUT, {
          workoutId: workoutAssignment.workout,
          clientId: workoutAssignment.user,
        });
      } else if (action === ActionType.UPDATE_SCHEDULED_WORKOUT) {
        // The edit is stored on the scheduled assignment and swapped in when it
        // fires, so the source workout is left as it is.
        await onSaveScheduledWorkoutDraft({
          [fieldName.NAME]: values[fieldName.NAME],
          [fieldName.NOTE]: values[fieldName.NOTE] || '',
          activities: preparedActivities.map((activity) => activity.jsonActivity),
          estimatedDuration: durationSeconds,
        });
        showToast(format(texts.scheduledWorkoutUpdated, { workoutName: values[fieldName.NAME] }));
      }

      logWorkoutTime({
        workoutId,
        workouAssignmentId: workoutAssignment?.id,
        isGeneratedByAI: generatedByAI,
        action,
      });

      // Update the workout request with the workout id when a workout is saved
      if (workoutRequestId && workoutId && (action === ActionType.CREATE || action === ActionType.UPDATE)) {
        await WorkoutRequest.updateDoc(workoutRequestId, { workoutId });
      }

      if (isComponentMountedRef.current) {
        setIsLoading(false);
        actions.setSubmitting(false);
        setFormikData({});
        setShowConfirmationModal(false);
        setShowAssignmentUpdateModal(false);
        setSharedWorkoutPrompt(null);
        setSelectedAssignmentUpdateOption(AssignmentUpdateType.SELECTED_ASSIGNMENT);
        setAssignmentsToUpdate([]);
        onClose();
        onSave(forkedWorkoutId, dayIndex);
      }
    } catch (error) {
      if (isComponentMountedRef.current) {
        setIsLoading(false);
        actions.setSubmitting(false);
        if (action === ActionType.UPDATE_SCHEDULED_WORKOUT) {
          /*
            The save reports why a scheduled edit was refused — most often that the assignment
            stopped being pending — so show that instead of the generic copy. Keyed off the error
            type rather than off `error.message`, so an internal throw from anywhere else in this
            try never reaches the coach as a toast.
          */
          showToast(
            isUserFacingError(error) ? error.message : format(texts.saveFailed, { type: texts.scheduledWorkout }),
            { error: true },
          );
        } else {
          const type = action === ActionType.UPDATE_WORKOUT_ASSIGNMENT ? texts.workoutAssignment : texts.workout;
          showToast(format(texts.saveFailed, { type }), { error: true });
        }
      }
    }
  }, [
    action,
    showToast,
    isComponentMountedRef,
    workout,
    coachId,
    onSave,
    preparedActivities,
    workoutAssignment,
    logCoachingActivity,
    onClose,
    assignmentsToUpdate,
    selectedAssignmentUpdateOption,
    generatedByAI,
    workoutRequestId,
    logWorkoutTime,
    isEditingPublicWorkout,
    durationOverrideMinutes,
    programDoc,
    dayIndex,
    onSaveScheduledWorkoutDraft,
    hasLongSessionWorkout,
    longSessionWorkout,
  ]);

  /*
    From the workout, not `docData`, which is still empty for a session created in this editor
    session — and absent publishStatus means READY, which would fail a session still uploading.
  */
  const publishStatus = longSessionWorkout?.publishStatus ?? docData.publishStatus;

  // A long session's activity is written by the server; `preparedActivities` is a mount-time snapshot.
  const longSessionActivities = longSessionWorkout?.activities;
  const workoutValidityText = useMemo(() => validateWorkoutContent({
    sessionType,
    publishStatus,
    activities: longSessionWorkout ? (longSessionActivities || []) : preparedActivities,
  }), [
    sessionType,
    publishStatus,
    preparedActivities,
    longSessionWorkout,
    longSessionActivities,
  ]);

  const showInvalidWorkoutWarning = useCallback((type = texts.workout) => {
    showToast(format(texts.draftSavingWarningMsg, { reason: workoutValidityText, type }), { warning: true });
  }, [showToast, workoutValidityText]);

  const initialActivities = useMemo(
    () => getInitialActivityItems({ workout, workoutAssignment, scheduledWorkoutDraft }),
    [workout, workoutAssignment, scheduledWorkoutDraft],
  );

  const normalizedInitialActivities = useMemo(() => JSON.stringify(
    initialActivities.map((activityItem) => new Activity(activityItem).jsonActivity),
  ), [initialActivities]);

  const normalizedCurrentActivities = useMemo(() => JSON.stringify(
    preparedActivities.map((a) => a.jsonActivity),
  ), [preparedActivities]);

  // Check if activities have changed
  const activitiesChanged = normalizedInitialActivities !== normalizedCurrentActivities;

  // If form is dirty or activities changed, there are unsaved changes
  const hasUnsavedChanges = isFormDirty || activitiesChanged;

  const handleWorkoutUpdateBeforeSaving = useCallback((isValidWorkout, values) => {
    // If the workout is not valid, show a warning message to the user, otherwise show the confirmation modal
    if (!isValidWorkout) {
      showInvalidWorkoutWarning();
      return;
    }
    if (isEditingPublicWorkout) {
      return;
    }
    // When editing a workout from within a program, check whether it is referenced by other programs or by other days
    // of this program. If shared, let the coach fork a private copy or update it everywhere. Skip a PUBLIC program the
    // coach doesn't own: that program is copied (not mutated) on save, so we must not re-point it here.
    if (programDoc && workout && !isUnownedPublicProgram(programDoc, coachDoc)) {
      const usage = getWorkoutUsageCounts({
        otherPrograms: coachPrograms.filter((prog) => prog.id !== programDoc.id),
        currentProgramDays: programWorkoutDays,
        workoutId: workout.id,
        currentDayIndex: dayIndex,
      });
      if (!!usage && (usage.otherProgramCount + usage.sameProgramOtherDayCount) > 0) {
        setSharedWorkoutPrompt({
          usage,
          defaultName: `${values[fieldName.NAME]} ${texts.sharedWorkoutSuffix}`,
        });
        return;
      }
    }
    setShowConfirmationModal(true);
  }, [
    showInvalidWorkoutWarning,
    isEditingPublicWorkout,
    coachDoc,
    programDoc,
    workout,
    coachPrograms,
    dayIndex,
    programWorkoutDays,
  ]);

  const onConfirmSharedEdit = useCallback(({ createNewWorkout, newName }) => {
    handleSubmit(formikData.values, formikData.actions, { createNewWorkout, newName });
  }, [
    formikData,
    handleSubmit,
  ]);

  const handleAssignmentUpdateBeforeSaving = useCallback(async (isValidWorkout) => {
    // If the workout assignment is not valid, show a warning message to the user, otherwise show the confirmation modal
    if (!isValidWorkout) {
      showInvalidWorkoutWarning(texts.workoutAssignment);
    } else {
      setIsLoading(true);
      try {
        const userAssignments = await WorkoutAssignment.getUserUnStartedWorkoutAssignmentsByWorkoutId(
          workoutAssignment.user,
          workoutAssignment.workout,
        );
        if (isComponentMountedRef.current) {
          setAssignmentsToUpdate(userAssignments.docs);
          setIsLoading(false);
          setShowAssignmentUpdateModal(true);
        }
      } catch (error) {
        if (isComponentMountedRef.current) {
          setIsLoading(false);
          showToast(format(texts.saveFailed, { type: texts.workoutAssignment }), { error: true });
        }
      }
    }
  }, [
    workoutAssignment,
    isComponentMountedRef,
    showToast,
    showInvalidWorkoutWarning,
  ]);

  const handleStartOver = useCallback(() => {
    setPreparedActivities(initialActivities.map((activityItem) => new Activity(activityItem)));
    setWorkoutRequestId('');
    setGeneratedByAI(!!workout?.generatedByAI);
  }, [workout, initialActivities]);

  /**
   * Throws away a long session: any transfer in flight, and the workout itself.
   * Distinct from cancelling the upload, which abandons the bytes and keeps the session.
   *
   * The workout is archived rather than deleted, which is how every other entity in the dashboard is
   * removed and keeps the `workout` collection free of client deletes. The uploaded object is left
   * where it is — reclaiming it is the server's business, and a client delete of a multi-gigabyte
   * file is one more way for discarding to fail at the only moment the coach cares about it.
   */
  const handleDiscardWorkout = useCallback(async () => {
    setShowDiscardConfirm(false);
    if (!longSessionWorkout) {
      onClose();
      return;
    }
    setIsLoading(true);
    try {
      cancelUpload(longSessionWorkout.id);
      /*
        The coach can discard while the last bytes are still in flight, and the Storage trigger
        refuses an archived workout — so a finalize that lands in that window is rejected server-side
        rather than reviving a session nobody wants.
      */
      await longSessionWorkout.discardLongSession(loggedInUserId);
      onClose();
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          message: 'Failed to discard a long-format session workout',
          workoutId: longSessionWorkout.id,
        },
      });
      showToast(texts.longSession.discardFailed, { error: true });
    } finally {
      if (isComponentMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [
    longSessionWorkout,
    cancelUpload,
    loggedInUserId,
    onClose,
    showToast,
    isComponentMountedRef,
  ]);

  const onSubmitClick = useCallback(async (values, actions) => {
    setFormikData({ values, actions });
    const isValidWorkout = workoutValidityText === WorkoutAssignmentCode.VALID_WORKOUT;
    // A scheduled workout is going out to the client as it stands, so it has to be
    // assignable — there is no draft to fall back to, and only this one date is edited.
    if (action === ActionType.UPDATE_SCHEDULED_WORKOUT) {
      if (isValidWorkout) {
        await handleSubmit(values, actions);
      } else {
        showInvalidWorkoutWarning(texts.scheduledWorkout);
      }
      return;
    }
    /*
      A long session skips every confirm dialog. It has no activities to propagate into assignments,
      and it must never reach the save-as-draft path, which would write a PENDING workout as an
      ordinary active one.
    */
    if (isLongSession) {
      if (isValidWorkout) {
        await handleSubmit(values, actions);
      } else {
        showInvalidWorkoutWarning();
      }
      return;
    }
    if (workout && !isEditingPublicWorkout) {
      handleWorkoutUpdateBeforeSaving(isValidWorkout, values);
    } else if (workoutAssignment) {
      await handleAssignmentUpdateBeforeSaving(isValidWorkout);
    } else if (!isValidWorkout) {
      setShowSaveAsDraftModal(true);
    } else {
      handleSubmit(values, actions);
    }
  }, [
    workout,
    workoutAssignment,
    handleSubmit,
    workoutValidityText,
    handleWorkoutUpdateBeforeSaving,
    handleAssignmentUpdateBeforeSaving,
    isEditingPublicWorkout,
    action,
    isLongSession,
    showInvalidWorkoutWarning,
  ]);

  /**
   * Blocks navigation when there are unsaved changes. Shows a confirmation modal on navigation attempts.
   */
  useEffect(() => {
    if (!hasUnsavedChanges) {
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
      return undefined;
    }
    if (!unblockRef.current) {
      unblockRef.current = history.block((tx) => {
        setPendingLocation(tx);
        setNavConfirmOpen(true);
        return false;
      });
    }
    return () => {
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
    };
  }, [hasUnsavedChanges, history]);

  /**
   * Executes the pending navigation after user confirmation, removing the blocker
   * and navigating to the stored location.
   */
  const proceedNavigation = useCallback(() => {
    const next = pendingLocation;
    if (unblockRef.current) {
      unblockRef.current();
      unblockRef.current = null;
    }
    setNavConfirmOpen(false);
    setPendingLocation(null);
    if (next) {
      history.push(next);
    }
  }, [history, pendingLocation]);

  const handleNavSave = useCallback(async () => {
    const values = formikRef.current?.values || {};
    const actions = {
      setSubmitting: formikRef.current?.setSubmitting || (() => { }),
    };
    await handleSubmit(values, actions);
    proceedNavigation();
  }, [handleSubmit, proceedNavigation]);

  const sectionTitle = useMemo(() => {
    if (isEditingPublicWorkout) {
      return texts.addWorkout;
    }
    if (action === ActionType.UPDATE) {
      return texts.editWorkout;
    }
    if (action === ActionType.CREATE) {
      return texts.addWorkout;
    }
    if (action === ActionType.UPDATE_SCHEDULED_WORKOUT) {
      return texts.editScheduledWorkout;
    }
    return texts.editWorkoutAssignment;
  }, [action, isEditingPublicWorkout]);

  const buttonTitle = useMemo(() => {
    if (isEditingPublicWorkout) {
      return texts.saveNewButton;
    }
    if (action === ActionType.UPDATE) {
      return texts.submitButton.saveWorkout;
    }
    if (action === ActionType.CREATE) {
      return texts.saveNewButton;
    }
    if (action === ActionType.UPDATE_SCHEDULED_WORKOUT) {
      return texts.submitButton.saveScheduledWorkout;
    }
    return texts.submitButton.saveWorkoutAssignment;
  }, [action, isEditingPublicWorkout]);

  const estimatedDurationSeconds = useMemo(
    () => estimateDuration(preparedActivities),
    [preparedActivities],
  );

  const autoMinutes = useMemo(
    () => Math.round(estimatedDurationSeconds / 60),
    [estimatedDurationSeconds],
  );
  const displayMinutes = durationOverrideMinutes !== null ? durationOverrideMinutes : autoMinutes;
  const isDurationOverridden = durationOverrideMinutes !== null;
  const workoutHasDistanceActivities = useMemo(
    () => hasDistanceActivities(preparedActivities),
    [preparedActivities],
  );

  // Reset any manual override when distance activities are removed from the workout
  useEffect(() => {
    if (!workoutHasDistanceActivities && durationOverrideMinutes !== null) {
      setDurationOverrideMinutes(null);
    }
  }, [workoutHasDistanceActivities, durationOverrideMinutes]);

  const onExerciseAddClick = (exercise) => {
    setSelectedExercise(exercise);
    setShowActivityModal(true);
  };

  return (
    <Container>
      <HeaderRow>
        <TitleContainer>
          <Title>
            {texts.title}
          </Title>
        </TitleContainer>
      </HeaderRow>
      <ContentContainer>
        <Formik
          initialValues={formInitialValues || startValues}
          validationSchema={validationSchema}
          onSubmit={onSubmitClick}
          enableReinitialize
          innerRef={formikRef}
        >
          {({ isSubmitting, dirty, values }) => {
            // Track if form is changed
            if (dirty !== isFormDirty) {
              setIsFormDirty(dirty);
            }
            return (
              <StyledForm className="workout-editor-form">
                <SectionContainer>
                  <SectionHeaderContainer>
                    <SectionTitle>{sectionTitle}</SectionTitle>
                  </SectionHeaderContainer>
                  <StyledSectionCompartment>
                    {action === ActionType.UPDATE && workout && isAdmin && (
                      <WorkoutSubDetailsContainer>
                        <InputLabel>{`${texts.workoutId}:`}</InputLabel>
                        <WorkoutIdValue>{workout.id}</WorkoutIdValue>
                      </WorkoutSubDetailsContainer>
                    )}
                    {canPickSessionType && (
                      <SessionTypeContainer>
                        <InputLabel>{`${texts.sessionType.label}:`}</InputLabel>
                        <StyledRadioButtonGroup
                          isRow
                          options={SESSION_TYPE_OPTIONS}
                          selectedOption={sessionType}
                          onOptionChange={setSelectedSessionType}
                        />
                      </SessionTypeContainer>
                    )}
                    <NameFieldRow>
                      <StyledFormikInput
                        name={fieldName.NAME}
                        label={`${texts.field[fieldName.NAME].label}:`}
                      />
                      {!isLongSession && (
                      <DurationContainer>
                        <InputLabel>
                          {`${texts.field[fieldName.DURATION].label}:`}
                        </InputLabel>
                        {workoutHasDistanceActivities ? (
                          <>
                            <DurationInputRow>
                              <DurationInput
                                type="number"
                                min="1"
                                value={displayMinutes || ''}
                                onChange={(e) => {
                                  const val = e.target.value === '' ? null : Number(e.target.value);
                                  setDurationOverrideMinutes(val);
                                }}
                                placeholder={autoMinutes || 0}
                              />
                              <DurationMinLabel>min</DurationMinLabel>
                            </DurationInputRow>
                            {isDurationOverridden && (
                              <DurationAutoHint onClick={() => setDurationOverrideMinutes(null)}>
                                {format(texts.durationAutoHint, { duration: formatSeconds(estimatedDurationSeconds) })}
                              </DurationAutoHint>
                            )}
                          </>
                        ) : (
                          <EstimatedDurationText>
                            {estimatedDurationSeconds ? formatSeconds(estimatedDurationSeconds) : texts.zeroDuration}
                          </EstimatedDurationText>
                        )}
                      </DurationContainer>
                      )}
                    </NameFieldRow>
                    <StyledFormikInput
                      name={fieldName.NOTE}
                      label={`${texts.field[fieldName.NOTE].label}:`}
                      multiline
                      rows={3}
                    />
                    {!!clipboard && (
                      <WorkoutSubDetailsContainer>
                        <InputLabel>
                          {format(texts.clipboardLabel, { type: clipboard.type })}
                        </InputLabel>
                        {!!clipboard.activity.name && (
                          <ClipboardName>{clipboard.activity.name}</ClipboardName>
                        )}
                        {clipboard.activity.activities?.map((ex, i) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <ClipboardExerciseItem key={i}>{ex.name}</ClipboardExerciseItem>
                        ))}
                      </WorkoutSubDetailsContainer>
                    )}
                  </StyledSectionCompartment>
                  <StyledSectionCompartment>
                    {isLongSession ? (
                      <LongSessionPanel
                        workoutDoc={longSessionWorkout}
                        coachId={coachId}
                        workoutName={values[fieldName.NAME]}
                        workoutNote={values[fieldName.NOTE]}
                        onWorkoutCreated={setCreatedLongSessionWorkout}
                      />
                    ) : (
                      <Activities
                        activities={preparedActivities}
                        onActivitiesChange={setPreparedActivities}
                        onClickAddActivity={onClickAddActivity}
                        selectedExercise={selectedExercise}
                        showActivityModal={showActivityModal}
                        setShowActivityModal={setShowActivityModal}
                        setSelectedExercise={setSelectedExercise}
                        onClickAIPrompt={onClickAIPrompt}
                      />
                    )}
                  </StyledSectionCompartment>
                  <SectionFooterContainer>
                    <SaveButton
                      disabled={isSubmitting || (isLongSession && !hasLongSessionWorkout)}
                      type="submit"
                    >
                      {isLongSession ? texts.longSession.saveDetails : buttonTitle}
                    </SaveButton>
                    <CancelButton onClick={canDiscardWorkout ? () => setShowDiscardConfirm(true) : onClose}>
                      {canDiscardWorkout ? texts.longSession.discardWorkout : texts.cancelButton}
                    </CancelButton>
                  </SectionFooterContainer>
                </SectionContainer>
              </StyledForm>
            );
          }}
        </Formik>
        <ScrollableContainer ref={ref}>
          {showAIPrompt && (
            <>
              <AIWorkoutGenerator
                onGenerate={({ activities, workoutRequestId: requestId }) => {
                  setPreparedActivities(activities);
                  setGeneratedByAI(true);
                  if (requestId) {
                    setWorkoutRequestId(requestId);
                  }
                }}
                onStartOver={handleStartOver}
                currentActivities={preparedActivities}
                workoutRequestId={workoutRequestId}
              />
            </>
          )}
          {showExercisesTable && (
            <ExercisesTable
              isWorkoutDesignerView
              handleActionClick={onExerciseAddClick}
            />
          )}
        </ScrollableContainer>
      </ContentContainer>
      <LoadingOverlay
        isLoading={isLoading}
        loadingText={texts.uploading}
      />
      {showConfirmationModal && (
        <ConfirmDialog
          isOpen={showConfirmationModal}
          onConfirm={() => handleSubmit(formikData.values, formikData.actions)}
          onCancel={() => setShowConfirmationModal(false)}
          dialogTexts={{
            title: texts.actionConfirmMessage,
          }}
        />
      )}
      {showSaveAsDraftModal && (
        <ConfirmDialog
          isOpen={showSaveAsDraftModal}
          onConfirm={() => handleSubmit(formikData.values, formikData.actions)}
          onCancel={() => setShowSaveAsDraftModal(false)}
          dialogTexts={{
            title: format(texts.draftSaveConfirmMsgTitle, { reason: workoutValidityText }),
            content: texts.draftSaveConfirmMsgContent,
          }}
        />
      )}
      {showAssignmentUpdateModal && (
        <DialogRoundedModal
          title={texts.assignmentUpdateModalTitle}
          actionButtons={(
            <>
              <SaveButton onClick={() => handleSubmit(formikData.values, formikData.actions)}>
                {texts.submitButton.updateWorkoutAssignments}
              </SaveButton>
              <CancelButton
                onClick={() => setShowAssignmentUpdateModal(false)}
              >
                {texts.cancelButton}
              </CancelButton>
            </>
          )}
          open={showAssignmentUpdateModal}
          onClose={() => setShowAssignmentUpdateModal(false)}
        >
          <StyledRadioButtonGroup
            options={assignmentsUpdateOptions(
              workoutAssignment.workoutContent.name,
              assignmentsToUpdate.length,
            )}
            onOptionChange={(type) => setSelectedAssignmentUpdateOption(type)}
            selectedOption={selectedAssignmentUpdateOption}
          />
        </DialogRoundedModal>
      )}
      {sharedWorkoutPrompt && (
        <SharedWorkoutDialog
          usage={sharedWorkoutPrompt.usage}
          defaultWorkoutName={sharedWorkoutPrompt.defaultName}
          onConfirm={onConfirmSharedEdit}
          onCancel={() => setSharedWorkoutPrompt(null)}
        />
      )}
      {showDiscardConfirm && (
        <ConfirmDialog
          isOpen={showDiscardConfirm}
          onConfirm={handleDiscardWorkout}
          onCancel={() => setShowDiscardConfirm(false)}
          dialogTexts={{
            title: texts.longSession.discardConfirmTitle,
            content: texts.longSession.discardConfirmContent,
          }}
        />
      )}
      {navConfirmOpen && (
        <ConfirmationModal
          open={navConfirmOpen}
          onClose={proceedNavigation}
          title={texts.navConfirmTitle}
          description={texts.navConfirmDescription}
          onConfirm={handleNavSave}
        />
      )}
    </Container>
  );
};

WorkoutEditor.propTypes = {
  workout: PropTypes.instanceOf(Workout),
  workoutAssignment: PropTypes.instanceOf(WorkoutAssignment),
  scheduledWorkoutDraft: PropTypes.object,
  action: PropTypes.string.isRequired,
  programDoc: PropTypes.object,
  dayIndex: PropTypes.number,
  programWorkoutDays: PropTypes.array,
  onSaveScheduledWorkoutDraft: PropTypes.func,
  onSave: PropTypes.func,
  onClose: PropTypes.func,
};

WorkoutEditor.defaultProps = {
  workout: null,
  workoutAssignment: null,
  scheduledWorkoutDraft: null,
  programDoc: null,
  dayIndex: null,
  programWorkoutDays: [],
  onSaveScheduledWorkoutDraft: () => { },
  onSave: () => { },
  onClose: () => { },
};

/*
  Observed because it reads `workout.data`, which is a firestorter observable that populates
  asynchronously. Without this the editor renders whatever the document held on its first frame and
  never reacts — an existing long session opened by link would show as a standard workout — and the
  document never becomes observed, so no realtime listener is opened for the processing tracker.
*/
export default observer(WorkoutEditor);
